/* DYNAMO · Warenkorb (Schublade und Seite /cart), Agent W, 06.10.2026.
   1. Mengen-Knöpfe „1 Stk. | 2 Stk. | 3 Stk. | ab 4 Stk.“ setzen die Menge der Zeile über Horizons cart-items-component (Ajax, ohne Neuladen).
   2. „Passt dazu: unsere Sets“ → „Hinzufügen“ legt 1 Stück per Ajax-Cart-API dazu und meldet es wie Horizon (CartLinesUpdateEvent),
      damit Schublade, Seite und Warenkorb-Symbol sich selbst neu zeichnen.
   Als Modul geladen; Shopify lädt es pro Seite nur einmal. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/** Horizon legt `Theme` als globale Konstante an (snippets/scripts.liquid), nicht als window-Eigenschaft. */
const wege = () => {
  /** @type {any} */
  const t = typeof Theme !== 'undefined' ? Theme : null;
  return { hinzufuegen: t?.routes?.cart_add_url || '/cart/add.js', korb: t?.routes?.cart_url || '/cart' };
};

if (!window.__dyWarenkorb) {
  window.__dyWarenkorb = true;

  // Horizon misst beim Öffnen der Schublade, ob die Zusammenfassung unten „kleben“ darf. Öffnet sie sich nach dem
  // Hinzufügen, ist der neue Inhalt oft noch nicht gezeichnet → Zusammenfassung rutscht nach unten. Darum nach jeder
  // Warenkorb-Änderung neu messen lassen (Horizons eigenes Ereignis „cart-section:restored“).
  document.addEventListener('shopify:cart:lines-update', (/** @type {any} */ e) => {
    const neuMessen = () => {
      const schublade = document.querySelector('cart-drawer-component');
      schublade?.dispatchEvent(new Event('cart-section:restored', { bubbles: true }));
    };
    Promise.resolve(e.promise)
      .catch(() => {})
      .finally(() => {
        setTimeout(neuMessen, 450);
        setTimeout(neuMessen, 1200);
      });
  });

  document.addEventListener('click', (e) => {
    const ziel = e.target instanceof Element ? e.target : null;
    if (!ziel) return;

    const stufe = ziel.closest('[data-dy-stufe]');
    if (stufe instanceof HTMLButtonElement) {
      e.preventDefault();
      mengeSetzen(stufe);
      return;
    }

    const plus = ziel.closest('[data-dy-set-add]');
    if (plus instanceof HTMLButtonElement) {
      e.preventDefault();
      setDazu(plus);
    }
  });
}

/** @param {HTMLButtonElement} knopf */
async function mengeSetzen(knopf) {
  if (knopf.classList.contains('ist-aktiv') || knopf.disabled) return;
  const komponente = /** @type {any} */ (knopf.closest('cart-items-component'));
  const line = Number(knopf.dataset.line);
  const menge = Number(knopf.dataset.menge);
  if (!komponente || !line || !menge) return;
  await customElements.whenDefined('cart-items-component');

  // sofort sichtbar machen, welcher Knopf gewählt ist; das Ergebnis kommt mit dem neu gezeichneten Warenkorb
  knopf.closest('.dy-wk-stufen')?.querySelectorAll('[data-dy-stufe]').forEach((k) => {
    k.classList.toggle('ist-aktiv', k === knopf);
    k.setAttribute('aria-pressed', String(k === knopf));
  });
  const zeile = knopf.closest('tr');
  const eingabe = /** @type {HTMLInputElement | null} */ (zeile?.querySelector('input[name="updates[]"]') ?? null);
  if (eingabe) eingabe.value = String(menge);

  if (typeof komponente.updateQuantity === 'function') {
    komponente.updateQuantity({ line, quantity: menge, action: 'change' });
  }
}

/** @param {HTMLButtonElement} knopf */
async function setDazu(knopf) {
  if (knopf.getAttribute('aria-busy') === 'true') return;
  const karte = knopf.closest('[data-dy-set]');
  const wahl = /** @type {HTMLSelectElement | null} */ (karte?.querySelector('[data-dy-set-wahl]') ?? null);
  const variante = wahl ? wahl.value : knopf.dataset.variantId;
  if (!variante) return;

  const fehlerFeld = knopf.closest('.dy-wk-sets')?.querySelector('[data-dy-set-fehler]');
  if (fehlerFeld instanceof HTMLElement) fehlerFeld.hidden = true;

  knopf.setAttribute('aria-busy', 'true');
  knopf.disabled = true;

  const sektionen = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);

  const zusage = CartLinesUpdateEvent.createPromise();
  knopf.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variante, quantity: 1 }],
      promise: zusage.promise,
    })
  );

  try {
    const antwort = await fetch(wege().hinzufuegen, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variante), quantity: 1 }], sections: sektionen.join(',') }),
    }).then((r) => r.json());

    const korb = await fetch(`${wege().korb}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());

    if (antwort.status) {
      zusage.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(korb),
        detail: { didError: true, items: korb.items, itemCount: 0, source: 'dy-warenkorb' },
      });
      knopf.dispatchEvent(new CartErrorEvent({ error: antwort.message || 'Hinzufügen fehlgeschlagen', code: 'INVALID' }));
      if (fehlerFeld instanceof HTMLElement) {
        fehlerFeld.textContent = antwort.description || antwort.message || 'Das hat leider nicht geklappt. Bitte versuch es noch einmal.';
        fehlerFeld.hidden = false;
      }
      return;
    }

    zusage.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(korb),
      detail: {
        items: korb.items,
        itemCount: 1,
        sections: antwort.sections,
        source: 'dy-warenkorb',
        didError: false,
      },
    });
  } catch (fehler) {
    zusage.reject(fehler);
    if (fehlerFeld instanceof HTMLElement) {
      fehlerFeld.textContent = 'Das hat leider nicht geklappt. Bitte versuch es noch einmal.';
      fehlerFeld.hidden = false;
    }
  } finally {
    if (knopf.isConnected) {
      knopf.removeAttribute('aria-busy');
      knopf.disabled = false;
    }
  }
}
